import { useRef, useCallback } from 'react';

import { Pencil, Trash2 } from 'lucide-react';
import { useDrag, useDrop } from 'react-dnd';

import { formatTitle } from '../../utils/common';

import type { DragItem, IAllMenu } from './model';

const ItemTypes = {
  SUB_MENU_ITEM: 'subMenuItem',
  MENU_ITEM: 'menuItem',
};

const DragHandleIcon = () => (
  <svg
    className="w-4 h-4 text-gray-400"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <circle cx="8" cy="6" r="1.5" />
    <circle cx="8" cy="12" r="1.5" />
    <circle cx="8" cy="18" r="1.5" />
    <circle cx="16" cy="6" r="1.5" />
    <circle cx="16" cy="12" r="1.5" />
    <circle cx="16" cy="18" r="1.5" />
  </svg>
);

export const SubMenuItem = ({
  item,
  index,
  parentId,
  onDrop,
  onEdit,
  onDelete,
}: {
  item: IAllMenu;
  index: number;
  parentId: number;
  onDrop: (parentId: number, dragIndex: number, hoverIndex: number) => void;
  onEdit: (id: number) => void;
  onDelete: (id: number, name: string) => void;
}) => {
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.SUB_MENU_ITEM,
    item: {
      index,
      parentId,
      type: ItemTypes.SUB_MENU_ITEM,
      id: item.adminMenuId,
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ isOver }, drop] = useDrop<DragItem, void, { isOver: boolean }>({
    accept: ItemTypes.SUB_MENU_ITEM,
    drop: (draggedItem: DragItem) => {
      if (
        draggedItem.type === ItemTypes.SUB_MENU_ITEM &&
        draggedItem.index !== index &&
        draggedItem.parentId === parentId
      ) {
        onDrop(parentId, draggedItem.index, index);
        draggedItem.index = index;
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  const ref = useRef<HTMLDivElement>(null);
  drag(drop(ref));

  const handleDragStart = useCallback((e: React.DragEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <div
      ref={ref}
      className={`
      px-3 py-2 mb-1 flex items-center justify-between select-none
      border border-gray-300 rounded bg-white
      shadow transition-all duration-200
      cursor-move
      ${isDragging ? 'opacity-40' : ''}
      ${isOver ? 'bg-gray-100 border-2 border-dashed border-gray-400' : ''}
    `}
    >
      <div className="flex items-center">
        <button
          type="button"
          className="mr-2 cursor-move"
          onDragStart={handleDragStart}
          draggable
        >
          <DragHandleIcon />
        </button>
        <span className="text-gray-800">{formatTitle(item.menuDisplay)}</span>
      </div>

      <div className="flex items-center space-x-2">
        <button
          type="button"
          onClick={() => onEdit(item.adminMenuId)}
          className="w-7 h-7 flex items-center justify-center rounded
               border border-brand-500 text-brand-500
               hover:bg-brand-500 hover:text-white transition cursor-pointer"
        >
          <Pencil size={12} />
        </button>
        <button
          type="button"
          onClick={() => onDelete(item.adminMenuId, item.menuDisplay)}
          className="w-7 h-7 flex items-center justify-center rounded
               border border-error-400 text-error-500
               hover:bg-error-500 hover:text-white transition cursor-pointer"
        >
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );
};
